// Copying a value: the button alone, a read-only field with the button beside
// it, or a block (a command, a config file) with the button over it. The
// button reads "Copied" for a moment after it copies.
import { useCallback, useEffect, useRef, useState } from "react";
import { buttonClass } from "./Button.jsx";
import { FIELD } from "./styles.js";

function useCopy(value) {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [value]);
    return [copied, copy];
}

export function CopyButton({ value, label = "Copy", ariaLabel = null, size = "sm", variant = "secondary", disabled = false, className = "" }) {
    const [copied, copy] = useCopy(value);
    return (
        <button type="button" onClick={copy} disabled={disabled} aria-label={ariaLabel ?? undefined} className={`${buttonClass(variant, size)} shrink-0 ${className}`}>
            {copied ? "Copied" : label}
        </button>
    );
}

export function CopyField({ label, value, mono = true, className = "" }) {
    return (
        <div className={`flex min-w-0 gap-[8px] ${className}`}>
            <input
                aria-label={label}
                type="text"
                readOnly
                value={value}
                onFocus={(event) => event.target.select()}
                className={`${FIELD} min-w-0 flex-1 ${mono ? "font-mono text-[12px]" : ""}`}
            />
            <CopyButton value={value} size="md" ariaLabel={`Copy ${label}`} />
        </div>
    );
}

// `highlight` marks one part of the value (a token pasted into a command).
function marked(value, highlight) {
    if (!highlight || !value.includes(highlight)) return value;
    return value.split(highlight).flatMap((part, index) => (index === 0 ? [part] : [
        <mark key={index} className="rounded-[3px] bg-[color-mix(in_srgb,var(--green)_28%,transparent)] px-[2px] text-[color:var(--ink)]">{highlight}</mark>,
        part,
    ]));
}

export function CopyBlock({ label, value, highlight = null, className = "" }) {
    return (
        <div className={`grid min-w-0 gap-[6px] ${className}`}>
            <div className="flex items-center justify-between gap-[8px]">
                <span className="text-[12.5px] font-medium text-[color:var(--ink)]">{label}</span>
                <CopyButton value={value} ariaLabel={`Copy ${label}`} />
            </div>
            <pre className="m-0 max-w-full whitespace-pre-wrap break-all rounded-[8px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[10px] font-mono text-[12px] leading-[1.55] text-[color:var(--ink)]">
                {marked(value, highlight)}
            </pre>
        </div>
    );
}
